import { isAxiosError, AxiosResponse } from "axios";
import axios from "../config/axios";
import { apiUrl } from "../native/nativeApiOrigin";
import { platformRef } from "../platform/platformRef";
import { generateUuid } from "../util/uuid";
import type { AdminUiContext } from "./adminChat";
import {
    ApiFile,
    ApiReference,
    ApiSheetRegion,
    ArtifactMeta,
    ToolSuggestion,
} from "../shared/interfaces";
import { openAuthenticatedSseResponse } from "../util/authenticatedFetch";
import { getStreamErrorMessage } from "../util/streamUtils";
import type { ApiConversation } from "../shared/interfaces";
import { ApiStreamEventType } from "../marvin/streamProtocol";
import {
    AuthenticatedSseTerminalError,
    createAuthenticatedSseStream,
} from "../marvin/authenticatedSse";
import type {
    ApiMessagePart,
    AskUserOption,
    ContextStatsEventData,
    MarvinActionFinalStatus,
    NavigationSuggestedEventData,
} from "../marvin/messageParts";
export type {
    ApiMessagePart,
    ApiMessagePartKind,
    ContextStatsEventData,
    NavigationSuggestedEventData,
    NavigationSuggestionKind,
    NavigationSuggestionRoute,
    ReferencePartPayload,
} from "../marvin/messageParts";
export { ApiStreamEventType } from "../marvin/streamProtocol";

// ---------------------------------------------------------------------------
// Types for the Run Streaming API
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Canonical message parts (V2)
// ---------------------------------------------------------------------------

interface PartCreatedEventData {
    part: ApiMessagePart;
}

/** A `part_updated` carries EITHER a full `payload` replace (non-text kinds: a question
 * answered, an action resolved) OR an `appendText` delta (the streaming text part). */
interface PartUpdatedEventData {
    seq: number;
    payload?: Record<string, unknown>;
    appendText?: string;
}

interface PartCompletedEventData {
    seq: number;
}

// ---------------------------------------------------------------------------
// Typed Event Data Payloads
// ---------------------------------------------------------------------------

interface StepEventData {
    code: string;
    message: string;
}

interface RetrievalEventData {
    topDown: number;
    bottomUp: number;
    flatToc: number;
}

interface ReferencesEventData {
    references: Record<string, ApiReference>;
    files: ApiFile[];
}

interface ReferencesDeltaEventData {
    references: Record<string, ApiReference>;
    files?: ApiFile[];
}

/** The sections one tool call read, admitted for the caller (`tool_touched`; Marvin Explore lights them). */
interface ToolTouchedEventData {
    call_id?: string;
    touched: {
        kind: string;
        id: string;
        sheetId?: string;
        title?: string;
        page?: number;
        hl?: [number, number, number, number];
    }[];
}

interface LlmEventData {
    scope?: string;
}

interface ContentBlockStartData {
    blockId: string;
}

interface ContentBlockDeltaData {
    blockId: string;
    delta: string;
}

interface ContentBlockStopData {
    blockId: string;
}

interface ThinkingDeltaData {
    delta: string;
}

interface ThinkingStopData {
    durationMs: number;
}

interface ToolCallStartData {
    name: string;
    call_id: string;
}

interface ToolCallDeltaData {
    call_id: string;
    delta: string;
}

interface ToolCallStopData {
    // `name`/`arguments` are not sent on the wire (the reducer accumulates args from
    // TOOL_CALL_DELTA), so they are optional; research stops carry only call_id + tag.
    name?: string;
    call_id: string;
    arguments?: string;
}

interface ToolResultData {
    name: string;
    call_id: string;
    result: string;
    error?: boolean;
    /** Optional structured payload alongside the model-facing `result` text
     * (e.g. an AdminChangeReport — detected via `data.kind`, never by parsing
     * `result`). */
    data?: Record<string, unknown>;
}

/**
 * A first-class artifact (image, PDF, …) generated during the run. The wire
 * shape matches {@link ArtifactMeta}; the reducer accumulates these into
 * `RunStreamState.artifacts` and they are persisted on the saved message so the
 * post-stream refetch renders them identically.
 */
type ArtifactReadyEventData = ArtifactMeta;

interface ToolSuggestionEventData {
    suggestions: ToolSuggestion[];
}

/**
 * The agent posted a question to the user, and the turn ENDS there: nothing waits on the
 * server. The card's answer is the user's next message (`ApiCreateRunRequest.answersQuestion`).
 * The event is persisted server-side and replayed when the SAME stream reconnects (the
 * stream's auto-reconnect with Last-Event-ID), deduped by `questionId` in the reducer.
 */
interface AskUserEventData {
    questionId: string;
    question: string;
    options: AskUserOption[];
    multiSelect: boolean;
    allowOther: boolean;
}

/**
 * A question was answered. No longer emitted: the answer is a new message, and the card's
 * answered state comes from the asking message's saved parts. Runs recorded before that
 * still replay it, so the reducers keep folding it.
 */
interface AskUserAnsweredEventData {
    questionId: string;
    answers: string[];
    otherText: string | null;
}

/**
 * The agent proposed an action that needs the user's approval before it runs. While the
 * action is pending the run is BLOCKED on the server (the action tool waits on a Postgres
 * NOTIFY, exactly like `ask_user`), so the rendered card is the only way the turn continues.
 * On approve the action EXECUTES inside the decision request under the user's own session.
 * The event is persisted server-side and replayed when the SAME stream reconnects (deduped
 * by `actionId` in the reducer). NOTE the V2 client opens a run's stream ONLY in the view
 * that started the run (`useRunStream.startRun`); a separate tab or a post-refresh fresh
 * mount does not re-attach to an in-flight run (it has no run id to attach to — the saved
 * message carries none), so it won't render a still-pending action until the turn completes
 * and the polled conversation refetch surfaces the saved message.
 */
interface ActionProposedEventData {
    actionId: string;
    /** Registry action_type (also the tool name); drives which action was proposed. */
    actionType: string;
    /** Human-readable heading (e.g. "Start submittal review"). */
    title: string;
    /** One-line, server-built description of exactly what will happen on approve. */
    summary: string;
    /** The validated action arguments, shown as a compact key-value list on the card. */
    payload: Record<string, unknown>;
    /** Optional preview image URL (e.g. a rendered thumbnail); null when the action has none. */
    previewImageUrl: string | null;
}

/**
 * A pending proposed action reached a terminal state — `executed` (approved + ran ok),
 * `failed` (approved but raised), `rejected` (user declined), or `expired` (15-min timeout).
 * Carries the outcome text so the open (originating) view flips its card out of the
 * pending/deciding state — off the live stream, or off that stream's reconnect replay
 * (deduped by `actionId` in the reducer). (The V2 client doesn't open the run stream in a
 * separate tab, so a foreign tab doesn't receive this live.)
 */
interface ActionResolvedEventData {
    actionId: string;
    status: MarvinActionFinalStatus;
    /** Human-readable outcome: the `execute()` return on success, the error on failure,
     *  else null (rejected / expired carry no result). */
    resultSummary: string | null;
}

/**
 * The Marvin agent saved to its persistent memory mid-turn. Emitted standalone the moment
 * the write lands and persisted so it replays on SSE reconnect exactly like
 * `artifact_ready` / `action_proposed` (deduped by seq in the reducer). It REPLACES the
 * write's generic tool-activity events — the backend suppresses those — so this event is
 * the ONLY transcript trace of the memory write.
 */
interface MemoryUpdatedEventData {
    /** Whose memory was written: the user's own (`user`) or the shared org-wide (`org`) file. */
    tier: "user" | "org";
    /** The memory file's path, e.g. `/memories/user/AGENTS.md`. */
    path: string;
    /** The file's NEW full contents (<=8KB). Absent in rare fallback cases — the card then
     * shows a muted "Memory file updated." line instead of the body. */
    content?: string;
}

interface DoneEventData {
    /** `joined`: only on a waiting run's stream, when the conversation's live run read its
     *  question; that run's answer covers it, and this run no longer exists. */
    status: "ok" | "cancelled" | "error" | "joined";
    /**
     * Client-only marker set on the synthetic `done` event emitted when the
     * SSE reconnect budget is exhausted (see `MAX_RECONNECTS` below). Lets
     * consumers (e.g. Conversation.tsx's foreground re-sync) distinguish
     * "gave up reconnecting" from a genuine server-reported run error, which
     * has the identical `isDone && status === "error"` shape. Never set on
     * real server-sent `done` events.
     */
    reconnectsExhausted?: boolean;
    /**
     * Client-only marker: this `done` was synthesized because the event stream's
     * connection ended (closed by a proxy, a failed reconnect, the reconnect budget),
     * not because the run ended. The run may still be going or waiting on the user, so
     * the stream owner re-attaches rather than ending the turn. Never set by the server.
     */
    connectionLost?: boolean;
    /** Client-only: the HTTP status that refused the stream, when a response did. */
    httpStatus?: number;
}

/** One of the person's Marvins that is working, as the queued panel lists it. */
export interface RunningMarvin {
    run_id: string;
    conversation_id: string | null;
    title: string;
    /** "chat" or "deep" (a Deep research run), or "sheet" (a Marvin Sheets row). */
    kind: "chat" | "deep" | "sheet";
    started_at: string | null;
}

/**
 * Where a run stands while it waits: sent on the stream of a run queued past the person's limit,
 * or sent to a conversation whose answer is still being written (`queued`); once more when it
 * starts (`started`); or, for a message the conversation's live run read, `joined`, naming that
 * run. These frames carry no id, so they never move the stream's resume cursor.
 */
export type RunStatusEventData =
    | {
          state: "queued";
          /** 1 is next. */
          position: number;
          /** Why it waits: the person's limit, the project's limit on Deep research ("Starting
           *  soon"), or the conversation's answer in progress. */
          reason: "user_limit" | "tenant_limit" | "conversation" | null;
          /** How many Marvins one person can have working at once. */
          limit: number;
          running: RunningMarvin[];
      }
    | { state: "started" }
    | {
          state: "joined";
          /** The conversation's live run, whose answer covers this message. */
          runId: string;
          marvinMessageId: string | null;
          /** The user message the live run read. */
          messageId: string;
      };

/** The live run read a message sent while it was working: its answer covers that message too.
 *  `runId` / `marvinMessageId` are the message's reserved run and placeholder, now gone. */
export interface MessageJoinedEventData {
    messageId: string;
    runId: string;
    marvinMessageId: string;
}

interface ErrorEventData {
    error: string;
    /** Client-only marker, as on {@link DoneEventData}: the connection ended, not the run. */
    connectionLost?: boolean;
    /** Client-only: the HTTP status that refused the stream, when a response did. */
    httpStatus?: number;
}

// ---------------------------------------------------------------------------
// Base Event Envelope (shared fields)
// ---------------------------------------------------------------------------

interface ApiStreamEventBase {
    v: number;
    seq: number;
    ts: string;
    streamId: string;
    requestId: string;
    conversationId?: string;
    parentMessageId?: string;
    marvinMessageId?: string;
}

// ---------------------------------------------------------------------------
// Discriminated Union of All Event Types
// ---------------------------------------------------------------------------

export type ApiStreamEvent =
    | (ApiStreamEventBase & { type: ApiStreamEventType.Step; data: StepEventData })
    | (ApiStreamEventBase & { type: ApiStreamEventType.Retrieval; data: RetrievalEventData })
    | (ApiStreamEventBase & {
          type: ApiStreamEventType.ThinkingStart;
          data: Record<string, unknown>;
      })
    | (ApiStreamEventBase & { type: ApiStreamEventType.ThinkingDelta; data: ThinkingDeltaData })
    | (ApiStreamEventBase & { type: ApiStreamEventType.ThinkingStop; data: ThinkingStopData })
    | (ApiStreamEventBase & { type: ApiStreamEventType.LlmStart; data: LlmEventData })
    | (ApiStreamEventBase & { type: ApiStreamEventType.LlmStop; data: LlmEventData })
    | (ApiStreamEventBase & { type: ApiStreamEventType.ReferencesStart; data: ReferencesEventData })
    | (ApiStreamEventBase & {
          type: ApiStreamEventType.ReferencesDelta;
          data: ReferencesDeltaEventData;
      })
    | (ApiStreamEventBase & {
          type: ApiStreamEventType.ToolTouched;
          data: ToolTouchedEventData;
      })
    | (ApiStreamEventBase & { type: ApiStreamEventType.ReferencesStop; data: ReferencesEventData })
    | (ApiStreamEventBase & {
          type: ApiStreamEventType.ContentBlockStart;
          data: ContentBlockStartData;
      })
    | (ApiStreamEventBase & {
          type: ApiStreamEventType.ContentBlockDelta;
          data: ContentBlockDeltaData;
      })
    | (ApiStreamEventBase & {
          type: ApiStreamEventType.ContentBlockStop;
          data: ContentBlockStopData;
      })
    | (ApiStreamEventBase & { type: ApiStreamEventType.ToolCallStart; data: ToolCallStartData })
    | (ApiStreamEventBase & { type: ApiStreamEventType.ToolCallDelta; data: ToolCallDeltaData })
    | (ApiStreamEventBase & { type: ApiStreamEventType.ToolCallStop; data: ToolCallStopData })
    | (ApiStreamEventBase & { type: ApiStreamEventType.ToolResult; data: ToolResultData })
    | (ApiStreamEventBase & {
          type: ApiStreamEventType.ArtifactReady;
          data: ArtifactReadyEventData;
      })
    | (ApiStreamEventBase & {
          type: ApiStreamEventType.ToolSuggestion;
          data: ToolSuggestionEventData;
      })
    | (ApiStreamEventBase & { type: ApiStreamEventType.AskUser; data: AskUserEventData })
    | (ApiStreamEventBase & {
          type: ApiStreamEventType.AskUserAnswered;
          data: AskUserAnsweredEventData;
      })
    | (ApiStreamEventBase & {
          type: ApiStreamEventType.ActionProposed;
          data: ActionProposedEventData;
      })
    | (ApiStreamEventBase & {
          type: ApiStreamEventType.ActionResolved;
          data: ActionResolvedEventData;
      })
    | (ApiStreamEventBase & {
          type: ApiStreamEventType.MemoryUpdated;
          data: MemoryUpdatedEventData;
      })
    | (ApiStreamEventBase & {
          type: ApiStreamEventType.NavigationSuggested;
          data: NavigationSuggestedEventData;
      })
    | (ApiStreamEventBase & { type: ApiStreamEventType.ContextStats; data: ContextStatsEventData })
    | (ApiStreamEventBase & { type: ApiStreamEventType.PartCreated; data: PartCreatedEventData })
    | (ApiStreamEventBase & { type: ApiStreamEventType.PartUpdated; data: PartUpdatedEventData })
    | (ApiStreamEventBase & {
          type: ApiStreamEventType.PartCompleted;
          data: PartCompletedEventData;
      })
    | (ApiStreamEventBase & { type: ApiStreamEventType.RunStatus; data: RunStatusEventData })
    | (ApiStreamEventBase & {
          type: ApiStreamEventType.MessageJoined;
          data: MessageJoinedEventData;
      })
    | (ApiStreamEventBase & { type: ApiStreamEventType.Done; data: DoneEventData })
    | (ApiStreamEventBase & { type: ApiStreamEventType.Error; data: ErrorEventData });

/** What to tell the person when a run could not be created: the server's own sentence when it sent one. */
export function runStartErrorMessage(err: unknown): string {
    const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response?.data
        ?.detail;
    if (typeof detail === "string" && detail) return detail;
    const message = (detail as { message?: unknown } | null | undefined)?.message;
    if (typeof message === "string" && message) return message;
    return err instanceof Error ? err.message : "Failed to start run";
}

/** One page-context entry attached to a run — mirrors ai_engine's PageContextEntry. */
interface ApiPageContextEntry {
    label: string;
    detail?: string;
    payload?: { kind: string; id: string };
    /** A spot on a drawing (a section id and a normalized page point), rendered after the handle. */
    point?: { drawingId: string; page: [number, number] };
}

/** The question card a run's message answers (ai_engine's `AnsweredQuestion`). */
export interface ApiAnsweredQuestion {
    messageId: string;
    questionId: string;
    answers: string[];
    otherText: string | null;
}

/** Request body for creating a run */
export interface ApiCreateRunRequest {
    query: string;
    /** "deep": a Deep research turn with a larger research budget. */
    depth?: "deep";
    context?: string;
    documentFilters?: string[];
    conversationId?: string;
    parentMessageId?: string;
    /** PrimeFile ids attached to the user message, injected inline by ai_engine */
    files?: string[];
    /** Drawing regions the question is about; the backend crops each and shows it to
     *  the model. (ai_engine still accepts a legacy singular `sheetRegion` from older
     *  clients; the frontend only ever sends this plural field.) */
    sheetRegions?: ApiSheetRegion[];
    /** Legacy singular drawing region sent by the flag-off (V2-disabled) chat path; the
     *  backend crops it and shows it to the model. V2 clients send `sheetRegions` instead. */
    sheetRegion?: ApiSheetRegion;
    /** What the user was looking at when they sent this message (drawer sends only);
     *  injected as a delimited text block on the agent's user turn. */
    pageContext?: ApiPageContextEntry[];
    /** The entities the message is about (a section, sheet, document page or record), by id;
     *  ai_engine validates each and hands them to Marvin's tools (CONTRACT_V31 §2). Uploads
     *  are never sent here: they ride in `files`. */
    contextRefs?: { kind: string; id: string; page?: number }[];
    /** The `ask_user` card this message answers: the question, the Marvin message that asked it and
     *  the choice. The message's own `query` is what the new turn reads; this marks the card answered. */
    answersQuestion?: ApiAnsweredQuestion;
    /** Selects a specialized server-side agent (e.g. "admin_edit"). Admin-gated
     * server-side — non-admins get a 403 when set. */
    agent?: string;
    /** Viewer-state snapshot for the admin-edit agent. Snake_case on the wire
     * (unlike the legacy camelCase fields) per the admin-chat wire contract. */
    ui_context?: AdminUiContext;
    /** Creates the run's conversation in the same request instead of `conversationId`
     *  (see `RunApiClient.createWithConversation`). */
    conversation?: ApiNewConversation;
    /** Idempotency key: a repeated create with the same key returns the first run. */
    operationId?: string;
}

/** The conversation `POST /runs` creates for its run (the fields of `POST /conversation`). */
export interface ApiNewConversation {
    message: string;
    messageType: string;
    documentFilters?: string[];
}

/**
 * Where a send went. `started` / `queued`: a new run (as before). With an answer already being
 * written in the conversation: `joined` (that run reads the message at its next step and its one
 * answer covers both), `next` (the message starts the next turn when that answer ends), or
 * `attached` (the text is a live question of the conversation: nothing new was created, and
 * the ids are that run's).
 */
export type RunPlacement = "started" | "queued" | "joined" | "next" | "attached";

/** Response from creating a run */
export interface ApiCreateRunResponse {
    /** The new run; for `joined` / `next` the run reserved for the message; for `attached`
     *  the live run. */
    runId: string;
    marvinMessageId: string;
    /** The saved user message (for `attached`, the live run's question). */
    userMessageId?: string | null;
    /** The run's conversation (servers since the one-request create always send it). */
    conversationId?: string;
    /** The conversation's full read (no messages), when this request created it. */
    conversation?: ApiConversation | null;
    /** "queued": the run waits (HTTP 202) and starts by itself; its stream reports
     *  `run_status` events. */
    status?: "queued" | null;
    /** The run's place in the line while queued; 1 is next. */
    position?: number | null;
    /** Where the send went; absent from servers before one live run per conversation. */
    placement?: RunPlacement | null;
    /** The conversation's live run and its answer message, for `joined` / `next` / `attached`. */
    liveRunId?: string | null;
    liveMarvinMessageId?: string | null;
}

/** A run created together with its conversation. */
export type ApiCreatedRunWithConversation = ApiCreateRunResponse & {
    conversationId: string;
    conversation: ApiConversation;
};

/** Debug event returned from the server */
export interface ApiDebugStreamEvent {
    id: string;
    objectType: string;
    objectId: string;
    eventType: ApiStreamEventType;
    eventJson: Record<string, unknown>;
    metaJson: Record<string, unknown> | null;
    createdAt: string;
}

/** Response from the debug stream-events endpoint */
interface ApiDebugStreamEventsResponse {
    events: ApiDebugStreamEvent[];
    /** The user message that started the answer, when the object is a message. */
    question?: string | null;
    usage?: { input_tokens: number; output_tokens: number; total_tokens: number };
}

const CONNECTION_LOST_MESSAGE =
    "Connection lost. Check your internet connection and try again please.";
const MAX_RECONNECTS = 5;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Emits the error+done pair for a stream this client could not keep: its connection closed
 * and could not reopen itself (a proxy's idle timeout, the reconnect budget), or the open was
 * refused (`httpStatus`) or answered with something that is not the run's event stream. All are
 * marked `connectionLost`: they end the stream, never the run, which may still be going, so
 * the stream's owner goes back to the server's state of the run instead of ending the turn
 * (hooks/useRunStream.ts). `reconnectsExhausted` marks the reconnect budget running out.
 */
function emitConnectionLost(
    onEvent: (event: ApiStreamEvent) => void,
    error: string,
    lost: { reconnectsExhausted?: boolean; httpStatus?: number } = {},
) {
    const marker = {
        connectionLost: true,
        ...(lost.httpStatus !== undefined ? { httpStatus: lost.httpStatus } : {}),
    };
    const envelope = { v: 1, seq: 0, streamId: "", requestId: "" };
    const ts = new Date().toISOString();
    onEvent({ ...envelope, ts, type: ApiStreamEventType.Error, data: { error, ...marker } });
    onEvent({
        ...envelope,
        ts,
        type: ApiStreamEventType.Done,
        data: {
            status: "error",
            ...(lost.reconnectsExhausted ? { reconnectsExhausted: true } : {}),
            ...marker,
        },
    });
}

/** True for the client-synthesized events of a lost connection (see emitConnectionLost). */
export {
    HEALTHY_STREAM_MS,
    isConnectionLostEvent,
    isStreamRefused,
    reopenDelayMs,
} from "../marvin/streamReopen";

/**
 * No byte on an open run stream for this long means its connection is dead: the server
 * writes a `: keepalive` comment every 15 s to readers that send `X-Access-Client`
 * (ai_engine `SSE_KEEPALIVE_SECONDS`), so three missed keepalives reconnect from the
 * last event instead of waiting on a socket that will never deliver `done`.
 */
const RUN_STREAM_IDLE_TIMEOUT_MS = 45_000;

/**
 * Run events always travel over fetch, never `EventSource`: a browser
 * `EventSource` cannot send the Access client-protocol headers, and ai_engine
 * answers a protected or enforced project's streams without them with 426.
 * `openAuthenticatedSseResponse` sends the same headers and credentials as
 * `authenticatedFetch` (the session cookie, or the embed/company JWT), and
 * `createAuthenticatedSseStream` resumes with `Last-Event-ID` and closes on `done`.
 */
function createRunStream(url: string, onEvent: (event: ApiStreamEvent) => void): () => void {
    return createAuthenticatedSseStream<ApiStreamEvent>({
        open: async ({ lastEventId, signal }) => {
            const { response, usedEmbedAuth } = await openAuthenticatedSseResponse(url, {
                signal,
                lastEventId,
                withCredentials: platformRef.current.auth.withCredentials,
            });
            if (!response.ok) {
                // An embedded session's refused bearer was already renewed once (or the
                // tab is being sent to sign in); the owner's next attempt tries again.
                const fallback =
                    response.status === 401 && !usedEmbedAuth
                        ? "Cannot validate session"
                        : CONNECTION_LOST_MESSAGE;
                throw new AuthenticatedSseTerminalError(
                    getStreamErrorMessage(await response.text(), fallback),
                    response.status,
                );
            }
            return response;
        },
        decode: (data) => JSON.parse(data) as ApiStreamEvent,
        isTerminal: (event) => event.type === ApiStreamEventType.Done,
        onEvent,
        maxReconnects: MAX_RECONNECTS,
        idleTimeoutMs: RUN_STREAM_IDLE_TIMEOUT_MS,
        // Whatever ended the stream, the run is the server's to end.
        onTerminalError: (message, reconnectsExhausted, httpStatus) =>
            emitConnectionLost(onEvent, message, { reconnectsExhausted, httpStatus }),
    });
}

// ---------------------------------------------------------------------------
// Run API Client
// ---------------------------------------------------------------------------

/**
 * POST that treats HTTP 409 as a benign "already resolved" signal: returns the response
 * on a clean 200, null on a 409, and rethrows anything else so the caller's card can
 * surface an inline error.
 */
async function postOr409(url: string, body: unknown): Promise<AxiosResponse | null> {
    try {
        return await axios.post(url, body);
    } catch (err) {
        if (isAxiosError(err) && err.response?.status === 409) {
            return null;
        }
        throw err;
    }
}

/** Result of deciding on a mid-turn proposed action. */
export interface DecideRunActionResult {
    /**
     * True when the server reported the action was already decided (HTTP 409) — e.g. a
     * double-click, or the 15-min timeout already expired it server-side. The caller should
     * resolve the card quietly; the run is already continuing (the authoritative final
     * status arrives via `action_resolved` on the open stream), so this is NOT an error.
     */
    alreadyDecided: boolean;
    /**
     * The final status the server reports on a clean 200 (`executed`/`failed` on approve,
     * `rejected` on decline). Null on a 409 — the authoritative status then rides the
     * `action_resolved` event instead.
     */
    status: MarvinActionFinalStatus | null;
}

/**
 * Decide on a mid-turn proposed action, unblocking the paused run. On approve the backend
 * EXECUTES the action inside this request under the user's own session and returns the
 * final status; on decline it marks the action rejected. NOTE the path segment is `/run/`
 * (singular) — deliberate, per the backend contract. A 200 resolves cleanly;
 * 409 (already decided) resolves as `alreadyDecided`; any other status throws so the card
 * can surface an inline error and re-enable its buttons.
 */
export async function decideRunAction(
    runId: string,
    actionId: string,
    approve: boolean,
): Promise<DecideRunActionResult> {
    return RunApi.decideAction(runId, actionId, approve);
}

export interface RunApiTransport {
    post<T = unknown>(url: string, body?: unknown): Promise<{ data: T }>;
    postOrConflict?<T = unknown>(url: string, body?: unknown): Promise<{ data: T } | null>;
    get<T = unknown>(
        url: string,
        config?: { params?: Record<string, unknown> },
    ): Promise<{ data: T }>;
    stream(
        runId: string,
        onEvent: (event: ApiStreamEvent) => void,
        options?: { afterSeq?: number },
    ): () => void;
}

export interface RunApiClient {
    create(request: ApiCreateRunRequest): Promise<ApiCreateRunResponse>;
    /**
     * Creates a conversation and its first run in one request (one round trip fewer
     * than `POST /conversation` then `POST /runs`), idempotent by a fresh operation id.
     * A server without the one-request create answers 422, and this falls back to the
     * two requests.
     */
    createWithConversation(
        request: Omit<ApiCreateRunRequest, "conversationId" | "conversation" | "operationId">,
        conversation: ApiNewConversation,
    ): Promise<ApiCreatedRunWithConversation>;
    stream(
        runId: string,
        onEvent: (event: ApiStreamEvent) => void,
        options?: { afterSeq?: number },
    ): () => void;
    cancel(runId: string): Promise<void>;
    /** Ask a streaming run to stop researching and answer from what it has. */
    answerNow(runId: string): Promise<boolean>;
    decideAction(runId: string, actionId: string, approve: boolean): Promise<DecideRunActionResult>;
    getDebugEvents(objectType: string, objectId: string): Promise<ApiDebugStreamEventsResponse>;
}

/**
 * Creates the run client against injected command and stream transports.
 * The singleton below keeps the current axios and authenticated-SSE behavior.
 */
export function createRunApi(transport: RunApiTransport): RunApiClient {
    const postOrConflict =
        transport.postOrConflict ??
        (<T>(url: string, body?: unknown) => transport.post<T>(url, body));
    return {
        // A run records the units its creating client renders (an approval waits only for a
        // client that can decide it); every request declares them (`api/clientDeclaration.ts`).
        create: async (request) => {
            const resp = await transport.post<ApiCreateRunResponse>("/api/ai/runs", request);
            return resp.data;
        },

        createWithConversation: async (request, conversation) => {
            try {
                const resp = await transport.post<ApiCreateRunResponse>("/api/ai/runs", {
                    ...request,
                    conversation,
                    operationId: generateUuid(),
                });
                const { conversationId, conversation: created } = resp.data;
                if (conversationId && created) {
                    return { ...resp.data, conversationId, conversation: created };
                }
            } catch (error) {
                // An older server requires conversationId (422) and created nothing.
                const status = (error as { response?: { status?: number } } | null)?.response
                    ?.status;
                if (status !== 422) throw error;
            }
            const created = await transport.post<ApiConversation>("/api/ai/conversation", {
                ...conversation,
                ...(request.agent ? { agent: request.agent } : {}),
            });
            const run = await transport.post<ApiCreateRunResponse>("/api/ai/runs", {
                ...request,
                conversationId: created.data.id,
            });
            return { ...run.data, conversationId: created.data.id, conversation: created.data };
        },

        stream: (runId, onEvent, options) => transport.stream(runId, onEvent, options),

        cancel: async (runId) => {
            await transport.post(`/api/ai/runs/${runId}/cancel`);
        },
        answerNow: async (runId) => {
            const response = await transport.post<{ requested?: boolean }>(
                `/api/ai/runs/${runId}/answer-now`,
            );
            return response?.data?.requested === true;
        },
        decideAction: async (runId, actionId, approve) => {
            const response = await postOrConflict<{ status?: unknown }>(
                `/api/ai/run/${runId}/action/${actionId}/decision`,
                { approve },
            );
            if (response === null) return { alreadyDecided: true, status: null };
            const status = response.data?.status;
            return {
                alreadyDecided: false,
                status:
                    status === "executed" ||
                    status === "failed" ||
                    status === "rejected" ||
                    status === "expired"
                        ? status
                        : null,
            };
        },

        getDebugEvents: async (objectType, objectId) => {
            const resp = await transport.get<ApiDebugStreamEventsResponse>(
                "/api/ai/debug/stream-events",
                {
                    params: { object_type: objectType, object_id: objectId },
                },
            );
            return resp.data;
        },
    };
}

function streamRunWithWebTransport(
    runId: string,
    onEvent: (event: ApiStreamEvent) => void,
    options?: { afterSeq?: number },
): () => void {
    const path =
        options?.afterSeq !== undefined
            ? `/api/ai/runs/${runId}/events?after_seq=${options.afterSeq}`
            : `/api/ai/runs/${runId}/events`;
    return createRunStream(apiUrl(path), onEvent);
}

const defaultRunTransport: RunApiTransport = {
    post: (url, body) => axios.post(url, body),
    postOrConflict: (url, body) => postOr409(url, body),
    get: (url, config) => axios.get(url, config),
    stream: streamRunWithWebTransport,
};

export const RunApi = createRunApi(defaultRunTransport);
